import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Flex, Box, Button } from '@chakra-ui/react';
import {
  setCurrentStep,
  useAppDispatch,
  useAppSelector,
  useLazyGetScheduleTableDataQuery,
} from '@spartalabs/pdc-core';
import TableCard from 'components/Cards/TableCard';
import format from 'date-fns/format';
import { DelayAnimation } from 'shared/DelayAnimation';
import { ModelFilters } from 'utils/constants';
import { checkDateRange, downloadFile } from 'utils/functions';
import { CancelButton } from '../CancelButton';
import { CancelModal } from '../CancelModal';

export const HomeSchedules = () => {
  const { globalDate } = useAppSelector(state => state.globalDataSlicer);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [showModal, setShowModal] = useState<{
    show: boolean;
    id: string;
    status?: string;
  }>({ show: false, id: '', status: '' });
  const [dateRange, setDateRange] = useState(checkDateRange(globalDate));

  const [
    fetchTable,
    {
      data: tableData,
      isLoading: isLoadingTable,
      isFetching: isFetchingTable,
      isError: isErrorTable,
    },
  ] = useLazyGetScheduleTableDataQuery();

  useEffect(() => {
    setDateRange(globalDate);
  }, [globalDate]);

  useEffect(() => {
    fetchTable({
      page: 1,
      quantity: 5,
      term: '',
      endDate: format(
        checkDateRange(dateRange)[0].endDate || new Date(),
        'yyyy-MM-dd',
      ),
      startDate: format(
        checkDateRange(dateRange)[0].startDate || new Date(),
        'yyyy-MM-dd',
      ),
    });

    setCurrentStep(1);
  }, []);

  const handleCancel = () => {
    setShowModal({ show: false, id: '' });
  };

  const handleNavigateNewSchecule = () => {
    dispatch(setCurrentStep(1));
    navigate('/novo-agendamento/veiculos');
  };

  const tableLabels: TableLabel[] = useMemo(
    () => [
      {
        title: 'Placa',
        type: 'text',
        accessor: 'plate',
        disableSorting: true,
      },
      {
        title: 'Nº de atendimento',
        type: 'text',
        accessor: 'id',
        disableSorting: true,
      },
      {
        title: 'Status Atendimento',
        type: 'text',
        accessor: 'serviceStatus',
        disableSorting: true,
      },
      {
        title: 'Data Abertura',
        type: 'year',
        accessor: 'openDate',
        disableSorting: true,
      },
      {
        title: 'Data de Agendamento',
        type: 'text',
        accessor: 'scheduleDate',
        disableSorting: true,
      },
      {
        title: 'Data Reagendamento',
        type: 'text',
        accessor: 'rescheduleDate',
        disableSorting: true,
      },
      {
        title: 'Cancelar',
        type: 'custom',
        component: id => <CancelButton setShowModal={setShowModal} id={id} />,
      },
    ],
    [],
  );

  return (
    <DelayAnimation>
      <Box mx="18px">
        <CancelModal
          id={showModal.id}
          obj={tableData}
          isOpen={showModal.show}
          onClose={handleCancel}
        />
        <Flex
          bg="white"
          py="24px"
          px="18px"
          rounded="base"
          justify="space-between"
          align="center"
          mb="20px"
        >
          <Text fontSize="18px" color="GrayText">
            Solicite um Agendamento de Manutenção
          </Text>
          <Button onClick={handleNavigateNewSchecule}>
            <Text fontSize="12px" fontWeight="light">
              Novo Agendamento
            </Text>
          </Button>
        </Flex>

        <TableCard
          hasActions
          loading={isLoadingTable || isFetchingTable}
          fetchData={fetchTable}
          fetchDownload={async () =>
            await downloadFile('schedule', 'Agendamentos')
          }
          hasGlobalSearch
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Relatórios de<b> todos os veículos</b>
            </Text>
          }
          subtitle="Acompanhe todos os detalhes da sua frota"
          overflowX="auto"
          w="100%"
          filters={ModelFilters}
          labels={tableLabels}
          data={tableData}
          setDateRange={setDateRange}
          initDate={dateRange}
          isError={isErrorTable}
          refetch={fetchTable}
        />
      </Box>
    </DelayAnimation>
  );
};
